import { createElement, type HTMLAttributes, type ReactNode } from 'react'
import type { Components, ExtraProps } from 'react-markdown'
import { FlowingText, isInlineFormattingOnly } from './FlowingText'

type FlowingTag = 'p' | 'li' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

const FLOWING_TAGS: Array<FlowingTag> = ['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']

export interface FlowingBlockOptions {
  shouldAnimate: boolean
  /** Rewrites a lone string child before it is flowed, e.g. to highlight mentions. */
  renderText?: (text: string) => ReactNode
  /** Wraps children in inert chunk spans for a reveal pass that animates them imperatively; wins over `shouldAnimate`. */
  chunkify?: (children: ReactNode) => ReactNode
  /** Flow a lone string child even when not animating, so the static and animated passes lay out alike. */
  flowStaticText?: boolean
  wordsPerChunk?: number
  fadeDuration?: number
}

function createFlowingBlock(
  tag: FlowingTag,
  { shouldAnimate, renderText, chunkify, flowStaticText = false, wordsPerChunk, fadeDuration }: FlowingBlockOptions,
) {
  function FlowingBlock({ node: _node, children, ...props }: HTMLAttributes<HTMLElement> & ExtraProps) {
    if (chunkify) {
      const rendered = typeof children === 'string' && renderText ? renderText(children) : children
      return createElement(tag, props, chunkify(rendered))
    }

    if (typeof children === 'string') {
      const rendered = renderText ? renderText(children) : children
      // Animating, a string goes through the same chunking as inline formatting, so a block that
      // gains a `**` mid-stream keeps its chunk keys and doesn't fade in again from the start.
      if (typeof rendered === 'string' && shouldAnimate) {
        return createElement(
          tag,
          props,
          <FlowingText shouldAnimate wordsPerChunk={wordsPerChunk} fadeDuration={fadeDuration}>
            {rendered}
          </FlowingText>,
        )
      }
      if (typeof rendered === 'string' && flowStaticText) {
        return createElement(
          tag,
          props,
          <FlowingText
            text={rendered}
            shouldAnimate={shouldAnimate}
            wordsPerChunk={wordsPerChunk}
            fadeDuration={fadeDuration}
          />,
        )
      }
      return createElement(tag, props, rendered)
    }

    if (shouldAnimate && isInlineFormattingOnly(children)) {
      return createElement(
        tag,
        props,
        <FlowingText shouldAnimate={shouldAnimate} wordsPerChunk={wordsPerChunk} fadeDuration={fadeDuration}>
          {children}
        </FlowingText>,
      )
    }

    return createElement(tag, props, children)
  }
  FlowingBlock.displayName = `FlowingBlock.${tag}`
  return FlowingBlock
}

/** `p`, `li` and the headings as markdown `components` whose plain text flows in word by word while `shouldAnimate`. */
export function createFlowingBlockComponents(options: FlowingBlockOptions): Components {
  return Object.fromEntries(FLOWING_TAGS.map((tag) => [tag, createFlowingBlock(tag, options)]))
}
